/**
 * منطقِ خالصِ لاگِ فعالیتِ توسعه: شکلِ ردیف، کوتاه کردنِ `detail`، و نامِ
 * خوانا برای هر رفت‌وبرگشتِ دیتابیس.
 *
 * از `devlog.ts` جدا شده به همان دلیلی که `analyticsQueue` از `analytics`
 * جدا شده: آن یکی تایمر و شبکه دارد و این یکی هیچ‌کدام، پس این با
 * `npm test` مستقیم اجرا می‌شود — بدونِ دیتابیس و بدونِ مرورگر.
 */

/**
 * شش نوعِ سطر. دقیقاً همان فهرستی که `check` جدول در مایگریشن 0071 دارد؛
 * اگر یکی این‌جا اضافه شود و آن‌جا نه، ردیف با خطای `check` برمی‌گردد.
 *
 * - `app`   — بالا آمدنِ اپ و بسته شدنش.
 * - `nav`   — رفتن به یک صفحه. `ms` یعنی از تعویضِ مسیر تا اولین فریمِ رسم‌شده.
 * - `tap`   — کاری که کاربر کرد. `ms` یعنی خودِ هندلر چقدر طول کشید.
 * - `query` — یک رفت‌وبرگشتِ دیتابیس. `ms` و `ok` هر دو پرند.
 * - `error` — هرچه از `reportError` رد شود.
 * - `console` — `console.warn`/`console.error`.
 */
export type DevLogKind = 'app' | 'nav' | 'tap' | 'query' | 'error' | 'console';

/** ردیفی که به جدولِ `dev_log` می‌رود (مایگریشن 0071). */
export interface DevLogRow {
  /** ساعتِ خودِ دستگاه. ساعتِ سرور را خودِ جدول با `default now()` می‌گذارد. */
  at: string;
  session_id: string;
  seq: number;
  surface: 'web' | 'mobile';
  kind: DevLogKind;
  name: string;
  ms: number | null;
  ok: boolean | null;
  detail: Record<string, unknown>;
  app_version: string | null;
  platform: string | null;
  /**
   * تا وقتی نمی‌دانیم کی وارد شده `null` می‌ماند و ردیف در بافرِ پیش‌از‌شناسایی
   * می‌نشیند؛ نگاه کن به `devlog.ts`. هیچ ردیفی با `null` فرستاده نمی‌شود —
   * ستونِ دیتابیس `not null` است.
   */
  user_id: string | null;
}

/* ─────────────────────────── کوتاه کردنِ detail ─────────────────────────── */

/** بیش از این تعداد کلید، خوانده نمی‌شود؛ فقط جدول را پر می‌کند. */
export const MAX_KEYS = 12;
/** و هر رشته تا این اندازه. پیامِ خطای پستگرس گاهی چند خط است. */
export const MAX_STRING = 300;
/**
 * سقفِ کلِ `detail` — بر حسبِ کاراکتر، نه بایت.
 *
 * ⚠️ چرا کنارِ دو سقفِ بالا این سومی هم لازم است: جدول یک
 * `check (pg_column_size(detail) < 4096)` دارد که **بایت** می‌شمارد. دوازده
 * کلید ضربدر سیصد کاراکتر یعنی ۳۶۰۰ کاراکتر، که اگر انگلیسی باشد ۳۶۰۰ بایت
 * است و رد می‌شود — ولی پیام‌های این پروژه اغلب فارسی‌اند و هر حرفِ فارسی دو
 * بایت است. همان ردیف می‌شود ۷۲۰۰ بایت، `check` می‌ترکد، و چون سطرها دسته‌ای
 * فرستاده می‌شوند، **کلِ دسته** با آن می‌افتد. یعنی یک پیامِ خطای بلندِ فارسی
 * می‌توانست نوزده سطرِ بی‌ربطِ دیگر را هم با خودش ببرد.
 *
 * هزار کاراکتر حتی با بدترین حالتِ سه بایت هم زیرِ سقف می‌ماند.
 */
export const MAX_TOTAL = 1_000;

/**
 * `detail` را به چیزی تبدیل می‌کند که مطمئناً JSON می‌شود و مطمئناً کوچک
 * می‌ماند.
 *
 * ═══ چرا این‌جا مثلِ `sanitizeProps` سخت‌گیر نیست ═══
 *
 * `analyticsQueue.sanitizeProps` هر رشته‌ی بلند و هر چیزِ شبیهِ ایمیل را دور
 * می‌اندازد، چون `app_events` رفتارِ **کاربران** است و آن قولِ حریمِ خصوصی
 * باید در کد قفل باشد. این جدول برعکس است: فقط یک حساب — حسابِ خودِ توسعه‌دهنده
 * — تویش می‌نویسد، و چیزی که دنبالش هستیم دقیقاً همان جزئیاتِ خام است («کدام
 * کوئری؟ چه خطایی؟»). دور انداختنِ متن این‌جا یعنی لاگی که نمی‌گوید چه شد.
 *
 * پس تنها کاری که می‌کند بستنِ اندازه است، نه پالودنِ محتوا.
 */
export function trimDetail(detail: Record<string, unknown> | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!detail) return out;
  let budget = MAX_TOTAL;
  for (const [k, v] of Object.entries(detail)) {
    if (Object.keys(out).length >= MAX_KEYS || budget <= 0) break;
    if (v === null || v === undefined) continue;
    budget -= k.length;
    if (typeof v === 'number') { if (Number.isFinite(v)) out[k] = v; continue; }
    if (typeof v === 'boolean') { out[k] = v; continue; }

    // شیء و آرایه به رشته تبدیل می‌شوند نه اینکه بیفتند: `{code: 42501}` که
    // نصفه‌نیمه هم باشد، از نبودنش بیشتر می‌گوید.
    let text: string;
    if (typeof v === 'string') text = v;
    else {
      try {
        text = JSON.stringify(v) ?? String(v);
      } catch {
        text = '[unserializable]';
      }
    }
    // کوچک‌ترینِ دو سقف: سقفِ خودِ این رشته، و آنچه از سقفِ کل مانده. کلیدِ
    // آخر بریده می‌شود نه اینکه بیفتد — نصفِ پیامِ خطا از هیچ‌اش بهتر است.
    const room = Math.min(MAX_STRING, budget);
    out[k] = text.length > room ? `${text.slice(0, room)}…` : text;
    budget -= Math.min(text.length, room);
  }
  return out;
}

/* ─────────────────────────── نامِ کوئری ─────────────────────────── */

/**
 * نامِ خوانا برای یک رفت‌وبرگشتِ دیتابیس: `events.select:mine`,
 * `rpc.bump_counter`.
 *
 * همین رشته کلیدِ گروه‌بندیِ گزارش است (`npm run log:pull` کندترین‌ها را با
 * همین می‌شمارد)، پس نباید چیزی داشته باشد که از یک صدا به صدای بعد فرق
 * کند — نه شناسه، نه فیلترها.
 *
 * ═══ چرا تکه‌ی سومی هم لازم شد (WHOSUP-209) ═══
 *
 * با `${جدول}.${عمل}` تنها، **ده** کوئریِ متفاوتِ صفحه‌ی کشف همگی یک اسم
 * داشتند: `events.select`. گزارش یک سطر می‌داد — «۲۹۰ بار، میانه ۵۹۰ms،
 * بدترین ۴۶۳۳ms» — که می‌گفت یک جایی کند است و هیچ‌وقت نمی‌گفت کجا. مثلِ
 * قبضِ برقی که فقط جمعِ کلِ خانه را بنویسد: می‌فهمی زیاد است، نمی‌فهمی کدام
 * وسیله.
 *
 * برچسب از `tagged()` در `lib/supabase.ts` می‌آید و اختیاری است: کوئریِ
 * بی‌برچسب دقیقاً همان نامِ قبلی را می‌گیرد.
 */
export const queryName = (table: string, op: string, label?: string): string =>
  `${table}.${op}${label ? `:${label}` : ''}`;

/**
 * جدول‌هایی که خودشان بخشی از دستگاهِ لاگ‌اند و لاگ کردنشان بی‌معنی است.
 *
 * `dev_log` از این‌ها **اجباری** است نه سلیقه‌ای: نوشتنِ هر سطر خودش یک
 * `insert` است، و لاگ کردنِ آن یک سطرِ تازه می‌سازد که باز نوشته می‌شود.
 * دو تای دیگر سلیقه‌ای‌اند — رفت‌وبرگشتِ واقعی‌اند، ولی نویزی که تولید
 * می‌کنند دقیقاً وقتی بیشترین است که داری دنبالِ یک خطا می‌گردی.
 */
const SILENT_TABLES = new Set(['dev_log', 'client_errors', 'app_events']);

export const isSilentTable = (table: string): boolean => SILENT_TABLES.has(table);

/* ─────────────────────────── نشست ─────────────────────────── */

/**
 * شناسه‌ی یک بار باز کردنِ اپ. کوتاه و خوانا، چون توی فایلِ لاگ کنارِ هر سطر
 * دیده می‌شود و باید بشود با چشم دو نشست را از هم جدا کرد.
 */
export const newDevSession = (): string => `${Date.now().toString(36)}-${
  Math.random().toString(36).slice(2, 6)}`.toUpperCase();
